<template>
  <div
    v-if="isVisible"
    :class="$style.popover"
    :style="popoverStyle"
    @click.stop
  >
    <div :class="$style.content">
      <div :class="$style.arrow" :style="arrowStyle"></div>
      <div :class="$style.text">{{ text }}</div>
      <button :class="$style.closeButton" @click="dismiss">
        <CloseIcon :size="12" />
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, type CSSProperties } from "vue";
import { CloseIcon } from "./icons";

const props = defineProps<{
  isVisible: boolean;
  text: string;
  targetElement?: HTMLElement;
  position?: "top" | "bottom" | "left" | "right";
  zIndex?: number;
}>();

const emit = defineEmits<{
  (e: "close"): void;
  (e: "dismiss"): void;
}>();

const popoverStyle = computed((): CSSProperties => {
  if (!props.targetElement) return {};

  const rect = props.targetElement.getBoundingClientRect();
  const position = props.position || "bottom";

  let top = 0;
  let left = 0;

  switch (position) {
    case "top":
      top = rect.top - 60;
      left = rect.left + rect.width / 2 - 50;
      break;
    case "bottom":
      top = rect.bottom + 10;
      left = rect.left + rect.width / 2 - 50;
      break;
    case "left":
      top = rect.top + rect.height / 2 - 25;
      left = rect.left - 120;
      break;
    case "right":
      top = rect.top + rect.height / 2 - 25;
      left = rect.right + 10;
      break;
  }

  return {
    position: "fixed",
    top: `${top}px`,
    left: `${left}px`,
    zIndex: props.zIndex || 100,
  };
});

const arrowStyle = computed((): CSSProperties => {
  const position = props.position || "bottom";

  switch (position) {
    case "top":
      return {
        bottom: "-8px",
        left: "50%",
        transform: "translateX(-50%)",
        borderTop: "8px solid #2c3e50",
        borderLeft: "8px solid transparent",
        borderRight: "8px solid transparent",
      };
    case "bottom":
      return {
        top: "-8px",
        left: "50%",
        transform: "translateX(-50%)",
        borderBottom: "8px solid #2c3e50",
        borderLeft: "8px solid transparent",
        borderRight: "8px solid transparent",
      };
    case "left":
      return {
        right: "-8px",
        top: "50%",
        transform: "translateY(-50%)",
        borderLeft: "8px solid #2c3e50",
        borderTop: "8px solid transparent",
        borderBottom: "8px solid transparent",
      };
    case "right":
      return {
        left: "-8px",
        top: "50%",
        transform: "translateY(-50%)",
        borderRight: "8px solid #2c3e50",
        borderTop: "8px solid transparent",
        borderBottom: "8px solid transparent",
      };
    default:
      return {};
  }
});

function dismiss() {
  emit("dismiss");
}
</script>

<style module>
.popover {
  position: fixed;
}

.content {
  position: relative;
  background: #2c3e50;
  color: white;
  padding: 12px 16px;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.1);
  min-width: 120px;
  max-width: 200px;
  text-align: center;
}

.arrow {
  position: absolute;
  width: 0;
  height: 0;
}

.text {
  font-size: 14px;
  font-weight: 500;
  line-height: 1.4;
  margin-right: 20px;
}

.closeButton {
  position: absolute;
  top: 8px;
  right: 8px;
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.6);
  cursor: pointer;
  padding: 2px;
  border-radius: 2px;
  transition: all 0.2s;
}

.closeButton:hover {
  background: rgba(255, 255, 255, 0.1);
  color: white;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .content {
    padding: 10px 14px;
    min-width: 100px;
    max-width: 180px;
  }

  .text {
    font-size: 13px;
  }
}

@media (max-width: 480px) {
  .content {
    padding: 8px 12px;
    min-width: 80px;
    max-width: 160px;
  }

  .text {
    font-size: 12px;
  }
}
</style>
